import * as echarts from "echarts";
import "echarts-gl";
import type { ChatOrchestrationWebSocketPayload } from "../services/chatService";
import { chartLayoutSize, normalizeChartOption } from "./chartNormalizers";
import { buildTableHtml, normalizeTablePayload } from "./tablePayload";
import { resolveRenderType } from "./widgetDisplay";

const EMAIL_CSS = `
  body { margin: 0; padding: 16px; font-family: Arial, Helvetica, sans-serif; color: #222; background: #fff; }
  h3 { margin: 0 0 12px; font-size: 16px; font-weight: 600; }
  p { margin: 0 0 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
  pre { margin: 0; padding: 12px; background: #f7f7f7; border: 1px solid #e5e5e5; border-radius: 6px;
        white-space: pre-wrap; word-break: break-word; font-family: Menlo, Consolas, monospace; font-size: 12px; }
  table { border-collapse: collapse; width: 100%; max-width: 100%; margin: 0; font-size: 13px; }
  th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { background: #f5f5f5; font-weight: 600; }
  img.chart { display: block; max-width: 100%; height: auto; border: 0; }
`.trim();

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function extractText(result: unknown): string {
  if (result == null) return "";
  if (typeof result === "string") return result;
  if (isRecord(result)) {
    if (typeof result.response === "string") return result.response;
    if (typeof result.message === "string") return result.message;
    if (typeof result.text === "string") return result.text;
  }
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

function extractHtmlDocument(result: unknown): string {
  const tableHtml = buildTableHtml(result);
  if (tableHtml) return tableHtml;
  const text = extractText(result);
  return text || "";
}

function wrapEmailDocument(titleHtml: string, bodyHtml: string): string {
  const title = titleHtml.trim() ? `<h3>${escapeHtml(titleHtml.trim())}</h3>` : "";
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>${EMAIL_CSS}</style>
</head>
<body>
${title}
${bodyHtml}
</body>
</html>`;
}

function extractChartOption(result: unknown): Record<string, unknown> | null {
  if (result == null) return null;
  let option: Record<string, unknown> = {};
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result) as Record<string, unknown>;
      option = (parsed.option != null ? parsed.option : parsed) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (isRecord(result)) {
    option = (result.option != null ? result.option : result) as Record<string, unknown>;
  } else {
    return null;
  }
  option = normalizeChartOption(option);
  const hasSeries = Array.isArray(option.series) && (option.series as unknown[]).length > 0;
  return hasSeries ? option : null;
}

function renderChartDataUrl(option: Record<string, unknown>): string | null {
  if (typeof document === "undefined") return null;
  const { height, minWidth } = chartLayoutSize(option, { maxHeight: 720, maxWidth: 960 });
  const width = Math.max(640, minWidth);
  const el = document.createElement("div");
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  el.style.position = "fixed";
  el.style.left = "-100000px";
  el.style.top = "0";
  el.style.pointerEvents = "none";
  el.setAttribute("aria-hidden", "true");
  document.body.appendChild(el);
  let chart: echarts.ECharts | null = null;
  try {
    chart = echarts.init(el, undefined, { renderer: "canvas", width, height });
    chart.setOption(option, true);
    return chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#ffffff",
    });
  } catch {
    return null;
  } finally {
    chart?.dispose();
    el.remove();
  }
}

function prettyJson(result: unknown): string {
  if (result == null) return "";
  if (typeof result === "string") {
    try {
      return JSON.stringify(JSON.parse(result), null, 2);
    } catch {
      return result;
    }
  }
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

export type ChatResultEmailPayload = Pick<
  ChatOrchestrationWebSocketPayload,
  "type" | "outputFormat" | "message" | "result"
>;

/**
 * Builds a self-contained HTML email body that mirrors the on-screen chat result.
 * Charts are embedded as PNG images; tables/html/text/json become styled HTML.
 */
export async function buildChatResultEmailHtml(
  payload: ChatResultEmailPayload
): Promise<string> {
  const type = resolveRenderType(payload.type, payload.outputFormat, payload.result);
  const message = typeof payload.message === "string" ? payload.message : "";

  if (type === "html") {
    const doc = extractHtmlDocument(payload.result).trim();
    if (!doc) {
      return wrapEmailDocument(message, "<p></p>");
    }
    if (/<!doctype html/i.test(doc) || /<html[\s>]/i.test(doc)) {
      // Full HTML report: keep as-is so email matches the iframe view.
      if (message.trim()) {
        return doc.replace(
          /<body([^>]*)>/i,
          (_m, attrs: string) => `<body${attrs}><h3>${escapeHtml(message.trim())}</h3>`
        );
      }
      return doc;
    }
    return wrapEmailDocument(message, doc);
  }

  if (type === "table") {
    const normalized = normalizeTablePayload(payload.result);
    const html =
      (normalized ? buildTableHtml(normalized) : "") ||
      buildTableHtml(payload.result) ||
      extractHtmlDocument(payload.result);
    return wrapEmailDocument(message, html.trim() || "<p>—</p>");
  }

  if (type === "chart") {
    const option = extractChartOption(payload.result);
    if (!option) {
      return wrapEmailDocument(message, "<p>Chart could not be rendered.</p>");
    }
    const dataUrl = await Promise.resolve(renderChartDataUrl(option));
    if (!dataUrl) {
      return wrapEmailDocument(
        message,
        `<pre>${escapeHtml(prettyJson(payload.result))}</pre>`
      );
    }
    return wrapEmailDocument(
      message,
      `<img class="chart" alt="Chart" src="${dataUrl}" />`
    );
  }

  if (type === "json") {
    return wrapEmailDocument(message, `<pre>${escapeHtml(prettyJson(payload.result))}</pre>`);
  }

  if (type === "text" || type === "error") {
    const text = extractText(payload.result) || message;
    return wrapEmailDocument(
      type === "error" ? message : "",
      `<p>${escapeHtml(text)}</p>`
    );
  }

  const fallback = extractText(payload.result) || message || prettyJson(payload);
  return wrapEmailDocument("", `<pre>${escapeHtml(fallback)}</pre>`);
}

export type ChatEmailBodySource = {
  content?: string;
  payload?: ChatResultEmailPayload | null;
  error?: string;
};

/**
 * Prefers a visual HTML mirror of the assistant payload; falls back to plain text.
 */
export async function buildChatEmailBodyFromMessage(
  message: ChatEmailBodySource
): Promise<{ body: string; html: boolean }> {
  if (message.error?.trim()) {
    return { body: message.error.trim(), html: false };
  }
  const payload = message.payload;
  if (!payload) {
    return { body: (message.content || "").trim(), html: false };
  }
  const type = String(payload.type || "").toLowerCase();
  if (type === "mutation-draft" || type === "clarification" || type === "widget-draft") {
    return { body: "", html: false };
  }
  const html = await buildChatResultEmailHtml(payload);
  return { body: html, html: true };
}
